import type { Task } from "@app/core";
import { Button, Card, ChevronDown, Eye, EyeOff, Repeat, cn } from "@app/ui";
import { memo, useState } from "react";

import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { ShowMoreFooter, usePagedRows } from "../../lib/paged-list.js";
import { useI18n } from "../../lib/useI18n.js";

interface Props {
  /** Tasks kept off the board/queue by `matrix_hidden` (see `hiddenMatrixTasks`). */
  tasks: Task[];
  onOpen: (task: Task) => void;
  /** Put the task's whole series back on the decision surfaces. */
  onShow: (task: Task) => void;
}

/**
 * Collapsed-by-default list of everything hidden from the matrix — the only way
 * back for a series taken off the board ("sleep", "work": real commitments that
 * occupy time but are not decisions). Deliberately NOT horizon-filtered, so a
 * hidden series whose next occurrence is months away is still reachable.
 * Renders nothing while nothing is hidden.
 */
export const HiddenPanel = memo(function HiddenPanel({ tasks, onOpen, onShow }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  // Collapsed by default, but nothing bounds how much a user has hidden — page
  // the body like every other list here so expanding it can't stall the route.
  const { shown, total, hasMore, showMore } = usePagedRows(tasks, null);

  if (tasks.length === 0) return null;

  return (
    <Card className="overflow-hidden p-0">
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-accent/30"
        onClick={() => setOpen((current) => !current)}
      >
        <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 break-words">
          {t("priorities.hidden.title", { count: tasks.length })}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={reduce ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0 } : springs.smooth}
            className="overflow-hidden border-t"
          >
            <p className="px-3 pt-2 text-xs text-muted-foreground">{t("priorities.hidden.hint")}</p>
            <ul className="flex flex-col divide-y divide-border text-sm">
              {shown.map((task) => (
                <li key={task.id} className="flex items-center gap-2 px-3 py-2">
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    title={t("tasks.tapToEdit")}
                    onClick={() => onOpen(task)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      onOpen(task);
                    }}
                  >
                    <span className="min-w-0 break-words">
                      {task.title || <em className="text-muted-foreground">{t("agenda.untitled")}</em>}
                    </span>
                    {task.recurrence_id && (
                      <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={t("agenda.recurring")} />
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 gap-1.5 px-2"
                    onClick={() => onShow(task)}
                    title={t("priorities.hidden.show")}
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">{t("priorities.hidden.show")}</span>
                  </Button>
                </li>
              ))}
            </ul>
            {hasMore && <ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} />}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
});
